/**
 * hooks/useOptionsQualifier.ts
 * ─────────────────────────────────────────────────────────────
 * Background hook that runs options qualification checks for
 * every gapper in the scanner list after a scan completes.
 *
 * Strategy:
 *  - Runs sequentially (not parallel) to avoid hammering Tradier
 *  - 300ms delay between each ticker to respect rate limits
 *  - Marks each ticker as 'loading' before fetching
 *  - Updates the store as each check completes (progressive rendering)
 *  - Skips tickers that already have a fresh check (< 5 min old)
 *  - Aborts if a new scan starts while checks are running
 * ─────────────────────────────────────────────────────────────
 */

import { useEffect, useRef, useCallback } from 'react';
import { useScanner, useAccount }         from '../store';
import { analysisApi }                    from '../lib/api';
import { qualifyOptions }                 from '../lib/optionsQual';
import type { OptionsQualCheck }          from '../types';

const DELAY_MS         = 400;   // ms between ticker fetches (rate limit buffer)
const CACHE_TTL_MS     = 5 * 60 * 1000; // 5 minutes — skip re-check if fresh

export function useOptionsQualifier() {
  const scanner  = useScanner();
  const account  = useAccount();
  const abortRef = useRef(false); // flip to true when a new scan starts, cancelling current run
  const wasScanning = useRef(false); // tracks isScanning to detect the true→false "scan just finished" edge

  const runChecks = useCallback(async (gappers: typeof scanner.gappers) => {
    if (!gappers.length) return;

    // Signal any in-flight run to stop
    abortRef.current = true;
    // Small tick to let any current iteration read the abort flag
    await new Promise(r => setTimeout(r, 50));
    abortRef.current = false;

    const accountBalance = account.stats?.balance ?? 2000;
    const now            = Date.now();

    for (const g of gappers) {
      if (abortRef.current) break; // new scan started — bail

      // Skip if we have a fresh check for this ticker
      const existing = scanner.optionsChecks[g.sym];
      if (existing && existing.status !== 'loading' && existing.status !== 'error') {
        const age = now - new Date(existing.checkedAt).getTime();
        if (age < CACHE_TTL_MS) continue;
      }

      // Mark as loading immediately so the UI shows a spinner
      scanner.setOptionsCheck(g.sym, {
        sym:        g.sym,
        status:     'loading',
        bestStrike: null, bestExpiry: null,
        spreadOk:   false, spreadVal: 0,
        oiOk:       false, oiVal:     0,
        deltaOk:    false, deltaVal:  0,
        budgetOk:   false, budgetVal: 0,
        liquidOk:   false,
        passCount:  0,
        failReasons: [],
        checkedAt:  new Date().toISOString(),
      } as OptionsQualCheck);

      try {
        const chain = await analysisApi.options(g.sym);
        if (abortRef.current) break;

        const check = qualifyOptions(g.sym, chain, accountBalance);
        scanner.setOptionsCheck(g.sym, check);
      } catch {
        if (abortRef.current) break;
        // On error, set a graceful failure state
        scanner.setOptionsCheck(g.sym, {
          sym:        g.sym,
          status:     'error',
          bestStrike: null, bestExpiry: null,
          spreadOk:   false, spreadVal: 0,
          oiOk:       false, oiVal:     0,
          deltaOk:    false, deltaVal:  0,
          budgetOk:   false, budgetVal: 0,
          liquidOk:   false,
          passCount:  0,
          failReasons: ['Options data unavailable'],
          checkedAt:  new Date().toISOString(),
        });
      }

      // Throttle between requests
      if (!abortRef.current) {
        await new Promise(r => setTimeout(r, DELAY_MS));
      }
    }
  }, [scanner, account.stats?.balance]);

  // Trigger on the isScanning true→false edge (a scan just finished) rather
  // than on scanner.gappers itself — with results now streaming in one
  // ticker at a time (see scanner.routes.ts / upsertGapper), the gappers
  // array gets a new reference on every single WS update mid-scan, which
  // would otherwise restart this whole sequential Tradier-throttled pass
  // dozens of times per scan instead of once at the end.
  useEffect(() => {
    const justFinished = wasScanning.current && !scanner.isScanning;
    wasScanning.current = scanner.isScanning;

    if (justFinished && scanner.gappers.length > 0) {
      scanner.clearOptionsChecks();
      runChecks(scanner.gappers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanner.isScanning]);

  return null; // this hook is side-effect only
}
